
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Logo />
            <Link 
              to="/" 
              className="flex items-center text-gray-600 hover:text-green-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Last updated: January 1, 2025</p>

          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Agreement to Terms</h2>
              <p className="text-gray-700">
                By accessing and using Sage Saver (sagesaver.net), you accept and agree to be bound by the 
                terms and provision of this agreement. If you do not agree to abide by the above, please 
                do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Description of Service</h2>
              <p className="text-gray-700">
                Sage Saver provides information about government assistance programs, private assistance programs, 
                financial planning resources, discounts, and coupons. Our services are designed specifically to 
                help low-income individuals and senior citizens access resources and save money.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">User Responsibilities</h2>
              <p className="text-gray-700 mb-3">By using our service, you agree to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Provide accurate and truthful information when signing up</li>
                <li>Use the information provided for legitimate purposes only</li>
                <li>Not share your account access with others</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Respect the intellectual property rights of Sage Saver and third parties</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Information Accuracy</h2>
              <p className="text-gray-700">
                While we strive to provide accurate and up-to-date information about assistance programs and 
                discounts, we cannot guarantee the accuracy, completeness, or timeliness of all information. 
                Program eligibility, availability, and terms may change. We recommend verifying information 
                directly with program administrators before applying.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Email and Text Communications</h2>
              <p className="text-gray-700 mb-3">
                By providing your email address and/or phone number and consenting to communications, you agree that:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>We may send you emails about assistance programs, discounts, and financial tips</li>
                <li>We may send you text messages about urgent opportunities (if you opt in)</li>
                <li>You can unsubscribe from emails at any time using the unsubscribe link</li>
                <li>You can stop text messages by replying STOP</li>
                <li>Message and data rates may apply for text messages</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Prohibited Uses</h2>
              <p className="text-gray-700 mb-3">You may not use our service to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Violate any local, state, national, or international law</li>
                <li>Transmit or send unsolicited commercial communications</li>
                <li>Impersonate any person or entity</li>
                <li>Interfere with or disrupt the service or servers</li>
                <li>Attempt to gain unauthorized access to our systems</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Disclaimers</h2>
              <p className="text-gray-700">
                Sage Saver is an informational service only. We do not guarantee eligibility for any assistance 
                programs or the availability of any discounts. We are not responsible for decisions made by 
                government agencies or private organizations regarding assistance applications. The information 
                provided is for educational purposes and should not be considered professional financial advice.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Limitation of Liability</h2>
              <p className="text-gray-700">
                Sage Saver shall not be liable for any indirect, incidental, special, consequential, or punitive 
                damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss 
                of data, use, goodwill, or other intangible losses resulting from your use of our service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Privacy Policy</h2>
              <p className="text-gray-700">
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use 
                of the service, to understand our practices. We will never sell or share your personal information 
                with third parties for marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Termination</h2>
              <p className="text-gray-700">
                We may terminate or suspend your access to our service immediately, without prior notice or 
                liability, if you breach these Terms of Service. You may also terminate your account at any 
                time by unsubscribing from our communications.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Changes to Terms</h2>
              <p className="text-gray-700">
                We reserve the right to modify or replace these Terms of Service at any time. If a revision 
                is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact Information</h2>
              <p className="text-gray-700">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-3 text-gray-700">
                <p>Email: support@sagesaver.net</p>
                <p>Website: sagesaver.net</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
